import React, { useRef } from 'react';
import { ThemeProvider } from 'styled-components';
import { observer } from 'mobx-react';
import { FormikHelpers } from 'formik';
import { format } from 'date-fns';

import { useViewport } from '@shared/hooks/useViewport';
import stores from '../../../stores';
import { CUSTOM_EXAM_CENTERS } from './constants';
import { commonTheme, customThemes } from './themes';
import { ReviewFormValues } from './types';
import { SmallResolution } from './SmallResolution/SmallResolution';
import { HighResolution } from './HighResolution/HighResolution';
import { RESOLUTION } from '../../../constants';

type ReviewFormProps = {
  examCenterId: string;
  partnerReviewPayloadId: string;
  email: string;
  firstName: string;
  lastName: string;
  examCenterTitle: string;
  examCenterLogo: string;
  examCenterAddress: string;
  placeId: string;
  type: string;
  city: string;
};

const ReviewForm = observer((props: ReviewFormProps) => {
  const reviewTextRef = useRef<HTMLTextAreaElement>(null);
  const { viewportWidth } = useViewport();

  const onFormSubmit = (values, { setErrors, setSubmitting }: FormikHelpers<ReviewFormValues>): void => {
    const {
      examCenterId, partnerReviewPayloadId, email, firstName, lastName,
    } = props;
    const examDate = format(values.examDate, 'yyyy-MM-dd');

    const reviewData = {
      exam_center_review: {
        exam_center_id: examCenterId,
        partner_review_payload_id: partnerReviewPayloadId,
        email,
        first_name: firstName,
        last_name: lastName,
        comment: values.textareaField,
        stars: values.stars,
        registration: values.registrationRate,
        waiting_room: values.waitingRoomRate,
        exam_room: values.examRoomRate,
        equipment: values.equipmentRate,
        result_availability: values.resultAvailabilityRate,
        exam_date: examDate,
      },
    };

    try {
      stores.examCenterReviewStore.create(reviewData, setErrors, setSubmitting);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const {
    examCenterTitle, examCenterLogo, examCenterAddress, type, firstName, email,
  } = props;
  const isCustomTheme = CUSTOM_EXAM_CENTERS.includes(type);

  return (
    <ThemeProvider theme={isCustomTheme ? customThemes[type] : commonTheme}>
      {
        viewportWidth > RESOLUTION.LARGE
          ? (
            <HighResolution
              isCustomTheme={isCustomTheme}
              firstName={firstName}
              type={type}
              examCenterLogo={examCenterLogo}
              examCenterAddress={examCenterAddress}
              examCenterTitle={examCenterTitle}
              onFormSubmit={onFormSubmit}
              reviewTextRef={reviewTextRef}
            />
          )
          : (
            <SmallResolution
              isCustomTheme={isCustomTheme}
              firstName={firstName}
              type={type}
              examCenterLogo={examCenterLogo}
              examCenterAddress={examCenterAddress}
              examCenterTitle={examCenterTitle}
              onFormSubmit={onFormSubmit}
              reviewTextRef={reviewTextRef}
              email={email}
            />
          )
      }
    </ThemeProvider>
  );
});

export { ReviewForm };
